// Probebetrieb: üben wie im echten Betrieb, nur hört niemand zu außer den
// Mithörern. Hier wird die Übung gestartet, mitgehört, besprochen und freigegeben.

import { useCallback, useEffect, useRef, useState } from "react";
import { api, type PracticeCheck, type PracticeOverview, type PracticeSession, type PracticeSessionDetail } from "../api";
import { useAuth } from "../auth/AuthProvider";
import { Icon, errorText } from "../components/streaming-ui";
import { Callout, EmptyState, Page, PageHeader, Skeleton } from "../components/ui";

function mmss(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function datum(iso: string): string {
  return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

const END_REASON: Record<string, string> = {
  manuell: "von Hand beendet",
  zeit: "Zeit war um",
  ruhe: "nichts mehr gesendet",
};

const HINT_STYLE: Record<string, string> = {
  gut: "callout-success",
  hinweis: "callout-warning",
  achtung: "callout-danger",
};

/** Nummerierte Schritte mit Zustand: erledigt, jetzt dran, später. */
function Steps({ steps }: { steps: Array<{ title: string; hint: string; state: "done" | "current" | "todo" }> }): JSX.Element {
  return (
    <ol className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
      {steps.map((st, i) => (
        <li
          key={st.title}
          className={`flex gap-3 rounded-xl border p-3.5 ${st.state === "current" ? "border-brand-primary/40 bg-brand-primary/[0.05] shadow-sm" : "border-default bg-card"}`}
        >
          <span
            className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums ${
              st.state === "done" ? "bg-emerald-500 text-white" : st.state === "current" ? "bg-brand-primary text-white shadow-glow" : "bg-input text-muted ring-1 ring-[var(--border)]"
            }`}
          >
            {st.state === "done" ? <Icon name="check" className="h-3.5 w-3.5" /> : i + 1}
          </span>
          <div className="min-w-0">
            <div className={`text-sm font-semibold ${st.state === "todo" ? "text-secondary" : "text-primary"}`}>{st.title}</div>
            <div className="mt-0.5 text-xs leading-snug text-muted">{st.hint}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

/** Farbe des Probebetriebs – bewusst anders als die Hausfarbe, damit niemand Probe und echt verwechselt. */
const PROBE = "#20c5c5";

export function PracticePage(): JSX.Element {
  const { user } = useAuth();
  const [data, setData] = useState<PracticeOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [minutes, setMinutes] = useState(120);
  const [open, setOpen] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    try {
      setData(await api.getPractice());
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }, []);
  useEffect(() => void load(), [load]);

  // Solange eine Übung läuft: Restzeit mitzählen und die Mithörer nachladen.
  const running = data?.running ?? null;
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      setNow(Date.now());
      void load();
    }, 10_000);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(t);
      clearInterval(tick);
    };
  }, [running, load]);

  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    try {
      await fn();
      await load();
      setError(null);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <Page>
        {error ? <Callout tone="danger">{error}</Callout> : <div className="space-y-4"><Skeleton className="h-16 w-80" /><Skeleton className="h-24" /><Skeleton className="h-40" /></div>}
      </Page>
    );
  }

  const isAdmin = user?.role === "tenant_admin" || user?.role === "super_admin";
  const leftMs = running ? new Date(running.ends_at).getTime() - now : 0;
  // Wo steht man gerade? Nur zur Orientierung – die Knöpfe unten entscheiden.
  const stepState = (done: boolean, reachable: boolean): "done" | "current" | "todo" => (done ? "done" : reachable ? "current" : "todo");
  const hatUebung = Boolean(running) || data.sessions.length > 0;
  const freigegeben = data.sessions.some((x) => x.approved_at);

  return (
    <Page className="max-w-6xl">
      <PageHeader
        eyebrow="Sendebetrieb"
        title={
          <span className="flex flex-wrap items-center gap-3">
            Probebetrieb
            {running && (
              <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold tracking-wider text-white" style={{ background: PROBE }}>
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" aria-hidden="true" /> PROBE LÄUFT
              </span>
            )}
          </span>
        }
        description={
          <>
            Hier üben neue Moderatorinnen und Moderatoren mit der echten Technik: gleiches Studio, gleiche Jingles, gleiche Stundenuhr –
            nur läuft die Sendung auf einem eigenen Übungskanal. Auf der Hörerseite taucht sie nicht auf. Zuhören kann nur, wer den
            Mithör-Link bekommt.
          </>
        }
      />

      <div className="space-y-6">
      <Steps
        steps={[
          { title: "Übungskanal", hint: "Eigener Kanal, nur während der Übung an.", state: stepState(Boolean(data.channel), true) },
          { title: "Zustimmen", hint: "Einmalig: Mitschnitt zur Besprechung.", state: stepState(Boolean(data.consent_at), Boolean(data.channel)) },
          { title: "Üben", hint: "Übung starten, Studio verbinden, senden.", state: stepState(hatUebung && !running, Boolean(data.channel && data.consent_at)) },
          { title: "Besprechen & freigeben", hint: "Nachhören, Notizen, Freigabe.", state: stepState(freigegeben, hatUebung && !running) },
        ]}
      />

      {error && <Callout tone="danger">{error}</Callout>}

      {!data.channel && (
        <section className="card space-y-3 p-5">
          <h2 className="card-title">Übungskanal fehlt noch</h2>
          <p className="text-sm leading-relaxed text-muted">
            Der Übungskanal ist ein eigener Kanal mit eigenem Live-Eingang und kleinem Ausgang (MP3, 64 kbit/s). Er läuft nur während
            einer Übung und kostet sonst nichts.
          </p>
          {isAdmin ? (
            <button
              disabled={busy}
              onClick={() => void run(() => api.createPracticeChannel())}
              className="btn btn-primary"
            >
              Übungskanal anlegen
            </button>
          ) : (
            <p className="text-sm text-muted">Ein Admin legt ihn mit einem Klick an.</p>
          )}
        </section>
      )}

      {data.channel && data.may_practice && !data.consent_at && (
        <section className="space-y-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.07] p-5">
          <h2 className="text-[0.95rem] font-semibold text-amber-900 dark:text-amber-200">Einmal zustimmen, dann kann es losgehen</h2>
          <p className="text-sm leading-relaxed text-amber-900 dark:text-amber-200/90">
            Jede Übung wird mitgeschnitten, damit sie hinterher besprochen werden kann. Der Mitschnitt liegt{" "}
            {data.recording_keep_days} Tage auf dem Server und wird dann automatisch gelöscht. Vorher löschen kannst du ihn
            jederzeit selbst.
          </p>
          <button
            disabled={busy}
            onClick={() => void run(() => api.givePracticeConsent())}
            className="btn btn-primary h-auto whitespace-normal py-2 text-left"
          >
            „Meine Übungssendung wird mitgeschnitten, damit sie besprochen werden kann.“
          </button>
        </section>
      )}

      {data.channel && running && (
        <section className="card overflow-hidden" style={{ boxShadow: `0 0 0 1.5px ${PROBE}, 0 12px 32px -12px ${PROBE}66` }}>
          <div className="flex flex-wrap items-center gap-3 px-5 py-4" style={{ background: `${PROBE}14` }}>
            <span className="rounded-md px-2 py-1 text-sm font-bold tracking-wider text-white" style={{ background: PROBE }}>PROBE</span>
            <div className="min-w-0">
              <div className="font-semibold text-primary">{running.user_name} übt</div>
              <div className="text-xs text-muted">seit {datum(running.started_at)}</div>
            </div>
            <div className="ml-auto flex items-center gap-6">
              <div className="text-right">
                <div className="text-[0.7rem] text-muted">noch</div>
                <div className="text-xl font-semibold tabular-nums text-primary">{mmss(leftMs)} <span className="text-xs font-normal text-muted">Minuten</span></div>
              </div>
              <div className="text-right">
                <div className="text-[0.7rem] text-muted">Mithörer</div>
                <div className="text-xl font-semibold tabular-nums text-primary">{running.listeners} <span className="text-xs font-normal text-muted">{running.listeners === 1 ? "hört" : "hören"} zu</span></div>
              </div>
            </div>
          </div>
          <div className="space-y-4 p-5">

          {running.listen_url && (
            <div className="well space-y-2.5">
              <div className="text-xs font-medium text-muted">Mithör-Link – gilt nur, solange diese Übung läuft:</div>
              <div className="flex flex-wrap items-center gap-2">
                <input readOnly value={running.listen_url} className="input min-w-0 flex-1 basis-60 font-mono !text-xs" />
                <button
                  onClick={() => void navigator.clipboard.writeText(running.listen_url ?? "")}
                  className="btn btn-secondary"
                >
                  <Icon name="copy" /> Kopieren
                </button>
              </div>
              <audio controls preload="none" src={running.listen_url} className="h-8 w-full" />
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button disabled={busy} onClick={() => void run(() => api.stopPractice())} className="btn btn-primary">
              Übung beenden
            </button>
            <button disabled={busy} onClick={() => void run(() => api.extendPractice(30))} className="btn btn-secondary">
              30 Minuten dranhängen
            </button>
          </div>
          </div>
        </section>
      )}

      {data.channel && data.may_practice && !running && (
        <section className="card space-y-3 p-5">
          <h2 className="card-title">Übung starten</h2>
          <p className="text-sm leading-relaxed text-muted">
            Der Übungskanal geht an, das Studio kann sich verbinden, der Mitschnitt läuft mit. Nach Ablauf der Zeit endet die Übung
            von selbst – und auch dann, wenn eine Weile gar nichts mehr gesendet wird.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <select value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} className="input !w-auto">
              {[30, 60, 120, 180, 240].map((m) => (
                <option key={m} value={m}>{m} Minuten</option>
              ))}
            </select>
            <button
              disabled={busy || !data.consent_at}
              onClick={() => void run(() => api.startPractice(minutes))}
              className="btn btn-primary"
            >
              Übung starten
            </button>
            {!data.consent_at && <span className="text-xs text-muted">Erst oben zustimmen.</span>}
          </div>
        </section>
      )}

      <section className="space-y-3">
        <h2 className="section-label">Bisherige Übungen</h2>
        {data.sessions.length === 0 ? (
          <EmptyState icon={<Icon name="clock" />} title="Noch keine." description="Nach der ersten Übung stehen hier Mitschnitt, Sendungscheck und Notizen." />
        ) : (
          <ul className="card divide-y divide-[var(--border)] overflow-hidden">
            {data.sessions.map((s) => (
              <li key={s.id} className={open === s.id ? "bg-[var(--bg-sunken)]" : ""}>
                <button
                  onClick={() => setOpen(open === s.id ? null : s.id)}
                  className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-5 py-3.5 text-left hover:bg-[var(--bg-hover)]"
                  aria-expanded={open === s.id}
                >
                  <span className="font-medium text-primary">{s.user_name}</span>
                  <span className="text-xs tabular-nums text-muted">{datum(s.started_at)}</span>
                  {s.recording_ms && <span className="text-xs tabular-nums text-muted">· {mmss(s.recording_ms)} Minuten</span>}
                  {s.end_reason && <span className="text-xs text-muted">· {END_REASON[s.end_reason] ?? s.end_reason}</span>}
                  {s.approved_at && <span className="badge badge-success">freigegeben</span>}
                  {!s.ended_at && <span className="badge font-semibold text-white" style={{ background: PROBE }}>läuft</span>}
                  <span className="ml-auto text-muted"><Icon name={open === s.id ? "up" : "down"} /></span>
                </button>
                {open === s.id && <SessionDetail id={s.id} isAdmin={isAdmin} onChange={load} />}
              </li>
            ))}
          </ul>
        )}
      </section>

      {data.is_mentor && (
        <section className="space-y-3">
          <h2 className="section-label">Anwärter</h2>
          {data.trainees.length === 0 && <div className="rounded-xl border border-dashed border-default px-5 py-6 text-center text-sm text-muted">Noch keine.</div>}
          {data.trainees.length > 0 && <ul className="card divide-y divide-[var(--border)] overflow-hidden">
            {data.trainees.map((t) => (
              <li key={t.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-3">
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-input text-xs font-semibold text-secondary ring-1 ring-[var(--border)]">{t.name.slice(0, 1).toUpperCase()}</span>
                <span className="min-w-0">
                  <span className="block font-medium text-primary">{t.name}</span>
                  <span className="block text-xs text-muted">{t.email}</span>
                </span>
                <span className={`ml-auto text-xs ${t.consent ? "text-muted" : "text-amber-700 dark:text-amber-300"}`}>
                  {t.consent ? `Zustimmung zum Mitschnitt am ${datum(t.consent)}` : "hat dem Mitschnitt noch nicht zugestimmt"}
                </span>
                {isAdmin && (
                  <button
                    disabled={busy}
                    onClick={() =>
                      window.confirm(`„${t.name}" zum Moderator machen? Danach darf sie oder er auf die echten Kanäle senden.`) &&
                      void run(() => api.promoteTrainee(t.id))
                    }
                    className="btn btn-secondary btn-sm"
                  >
                    Zu Moderator machen
                  </button>
                )}
              </li>
            ))}
          </ul>}
          {isAdmin && <NewTrainee onDone={load} />}
        </section>
      )}
      </div>
    </Page>
  );
}

/** Zugang für einen Anwärter anlegen: Der Admin vergibt ein erstes Passwort und gibt es weiter. */
function NewTrainee({ onDone }: { onDone: () => Promise<void> }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", display_name: "", password: "" });
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="btn btn-secondary">
        <Icon name="plus" /> Anwärter anlegen
      </button>
    );
  }
  return (
    <div className="card space-y-3 p-5">
      <h3 className="card-title">Anwärter anlegen</h3>
      <p className="text-sm leading-relaxed text-muted">
        Der Zugang darf nur üben: Übungskanal, Studio, eigener Mitschnitt. An die echten Kanäle, die Mediathek oder den Sendeplan
        kommt er nicht. Das erste Passwort gibst du persönlich weiter – ändern kann er es selbst unter „Mein Konto“.
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="E-Mail" className="input" />
        <input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Name" className="input" />
        <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Erstes Passwort (mind. 10 Zeichen)" className="input" />
      </div>
      {error && <Callout tone="danger">{error}</Callout>}
      {done && <Callout tone="success">{done}</Callout>}
      <div className="flex gap-2">
        <button
          disabled={!form.email.trim() || form.password.length < 10}
          onClick={() =>
            void api
              .createTrainee({ email: form.email.trim(), display_name: form.display_name.trim() || undefined, password: form.password })
              .then((t) => {
                setDone(`${t.name} kann sich jetzt anmelden.`);
                setForm({ email: "", display_name: "", password: "" });
                setError(null);
              })
              .then(onDone)
              .catch((err) => setError(errorText(err)))
          }
          className="btn btn-primary"
        >
          Anlegen
        </button>
        <button onClick={() => setOpen(false)} className="btn btn-ghost">
          Schließen
        </button>
      </div>
    </div>
  );
}

/** Aufgeklappte Übung: Sendungscheck, Mitschnitt zum Nachhören, Notizen, Freigabe. */
function SessionDetail({ id, isAdmin, onChange }: { id: string; isAdmin: boolean; onChange: () => Promise<void> }): JSX.Element {
  const [session, setSession] = useState<PracticeSessionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [noteText, setNoteText] = useState("");
  const [busy, setBusy] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const load = useCallback(async () => {
    try {
      setSession(await api.getPracticeSession(id));
    } catch (err) {
      setError(errorText(err));
    }
  }, [id]);
  useEffect(() => void load(), [load]);

  // Blob-Adresse wieder freigeben, wenn die Übung zugeklappt wird.
  useEffect(() => () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
  }, [audioUrl]);

  const seek = (ms: number): void => {
    if (audioRef.current) {
      audioRef.current.currentTime = ms / 1000;
      void audioRef.current.play();
    }
  };

  if (!session) return <div className="border-t border-default px-5 py-4 text-sm text-muted">{error ?? "Lädt …"}</div>;

  const check: PracticeCheck | null = session.check;

  return (
    <div className="space-y-5 border-t border-default px-5 py-5">
      {error && <Callout tone="danger">{error}</Callout>}

      {session.recording && (
        <div className="space-y-2">
          {audioUrl ? (
            <audio ref={audioRef} controls src={audioUrl} className="h-8 w-full" />
          ) : (
            <button
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void api
                  .practiceRecordingUrl(id)
                  .then(setAudioUrl)
                  .catch((err) => setError(errorText(err)))
                  .finally(() => setBusy(false));
              }}
              className="btn btn-secondary"
            >
              {busy ? "Mitschnitt wird geladen …" : "Mitschnitt nachhören"}
            </button>
          )}
          <div className="text-xs text-muted">
            {session.recording_expires_at ? `Wird am ${datum(session.recording_expires_at)} automatisch gelöscht.` : ""}{" "}
            <button
              onClick={() =>
                window.confirm("Mitschnitt jetzt löschen? Das lässt sich nicht rückgängig machen.") &&
                void api
                  .deletePracticeRecording(id)
                  .then(load)
                  .then(onChange)
                  .catch((err) => setError(errorText(err)))
              }
              className="font-medium text-red-600 hover:underline dark:text-red-400"
            >
              Jetzt löschen
            </button>
          </div>
        </div>
      )}

      {check ? (
        <div className="space-y-2">
          <h3 className="section-label">Sendungscheck</h3>
          <ul className="space-y-1.5">
            {check.hints.map((h, i) => (
              <li key={i} className={`callout block !py-2 ${HINT_STYLE[h.kind] ?? ""}`}>
                {h.at_ms !== undefined && (
                  <button onClick={() => seek(h.at_ms ?? 0)} className="mr-2 rounded-md bg-card/70 px-1.5 py-0.5 font-mono text-xs tabular-nums ring-1 ring-black/10 hover:bg-card dark:ring-white/10">
                    {mmss(h.at_ms)}
                  </button>
                )}
                {h.text}
              </li>
            ))}
          </ul>
          <dl className="grid grid-cols-2 gap-2 pt-1 text-sm tabular-nums text-primary sm:grid-cols-4 [&>div]:rounded-lg [&>div]:bg-card [&>div]:px-3 [&>div]:py-2 [&>div]:ring-1 [&>div]:ring-[var(--border)] [&_dd]:mt-0.5 [&_dd]:font-semibold">
            <div><dt className="text-xs text-muted">Länge</dt><dd>{mmss(check.duration_ms)}</dd></div>
            <div><dt className="text-xs text-muted">Lautheit (gemessen)</dt><dd>{check.loudness_lufs?.toFixed(1) ?? "–"} LUFS</dd></div>
            <div><dt className="text-xs text-muted">Spitzen (gemessen)</dt><dd>{check.true_peak_db?.toFixed(1) ?? "–"} dBTP</dd></div>
            <div>
              <dt className="text-xs text-muted">Sprechanteil (vom Studio gemeldet)</dt>
              <dd>{check.speech_share === null ? "nicht gemeldet" : `${Math.round(check.speech_share * 100)} %`}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted">Längste Moderation</dt>
              <dd>{check.longest_speech_ms === null ? "–" : mmss(check.longest_speech_ms)}</dd>
            </div>
            <div><dt className="text-xs text-muted">Aus dem Sendeprotokoll</dt><dd>{check.tracks} Titel, {check.jingles} Jingles</dd></div>
            <div><dt className="text-xs text-muted">Stille über 2 s</dt><dd>{check.silence.length}</dd></div>
            <div><dt className="text-xs text-muted">Harte Schnitte</dt><dd>{check.hard_cuts_ms.length}</dd></div>
          </dl>
        </div>
      ) : (
        <p className="text-sm text-muted">{session.ended_at ? "Der Sendungscheck wird gerade gerechnet." : "Der Sendungscheck kommt, sobald die Übung zu Ende ist."}</p>
      )}

      <div className="space-y-2">
        <h3 className="section-label">Notizen</h3>
        {session.notes.length === 0 ? (
          <p className="text-sm text-muted">Noch keine.</p>
        ) : (
          <ul className="space-y-1">
            {session.notes.map((n) => (
              <li key={n.id} className="flex items-start gap-2 text-sm text-secondary">
                <button onClick={() => seek(n.at_ms)} className="rounded-md bg-brand-primary/10 px-1.5 py-0.5 font-mono text-xs tabular-nums text-brand-primary hover:bg-brand-primary/20">{mmss(n.at_ms)}</button>
                <span className="flex-1">{n.text}</span>
                <span className="text-xs text-muted">{n.author_name}</span>
              </li>
            ))}
          </ul>
        )}
        {session.may_note && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-muted">bei {mmss((audioRef.current?.currentTime ?? 0) * 1000)}</span>
            <input
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Was ist dir aufgefallen?"
              className="input min-w-0 flex-1 basis-48"
            />
            <button
              disabled={!noteText.trim()}
              onClick={() =>
                void api
                  .addPracticeNote(id, (audioRef.current?.currentTime ?? 0) * 1000, noteText.trim())
                  .then(() => setNoteText(""))
                  .then(load)
                  .catch((err) => setError(errorText(err)))
              }
              className="btn btn-secondary"
            >
              Notiz merken
            </button>
          </div>
        )}
      </div>

      {session.approved_at ? (
        <p className="callout callout-success block">
          Freigegeben am {datum(session.approved_at)} von {session.approved_by_name}.
          {isAdmin && " Oben in der Liste der Anwärter kann daraus jetzt ein Moderator werden."}
        </p>
      ) : (
        session.may_approve && (
          <button
            onClick={() =>
              void api
                .approvePracticeSession(id)
                .then(load)
                .then(onChange)
                .catch((err) => setError(errorText(err)))
            }
            className="btn btn-primary"
          >
            <Icon name="check" /> Probesendung freigeben
          </button>
        )
      )}
    </div>
  );
}
